/**
 * Teacher-Class Assignment Tab
 *
 * Matrix view: rows = teachers, columns = classes.
 * Local state mirrors server — checkboxes respond instantly.
 * Changes are batched and saved with a single "Save" action.
 */

import {
  Box, Typography, CircularProgress, Alert, TextField,
  InputAdornment, Paper, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Checkbox, Chip,
  Tooltip, Button, Stack, LinearProgress,
  alpha, useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTeachersDirect,
  getClassesDirect,
  getTeacherClassAssociations,
  createTeacherClassAssociation,
  deleteTeacherClassAssociation,
  getAcademicYearsDirect,
} from '@timetable/api';
import { queryKeys } from '../../lib/queryKeys';
import { useToast } from '../Toast';

interface Props { schoolId: string }

// key: "teacherId|classId"
type AssocSet = Set<string>;

export function TeacherClassAssignmentTab({ schoolId }: Props) {
  const theme = useTheme();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Local draft state — what the user sees
  const [draft, setDraft] = useState<AssocSet>(new Set());
  // Server state snapshot — used to compute diff on save
  const [serverState, setServerState] = useState<Map<string, string>>(new Map()); // key -> assoc id
  // Track which (schoolId, year) combo the draft was last synced from
  const syncedForRef = useRef<string>('');

  const { data: academicYears = [], isLoading: loadingYears } = useQuery({
    queryKey: queryKeys.academicYears.list({ school_id: schoolId }),
    queryFn: () => getAcademicYearsDirect({ school_id: schoolId }),
    enabled: !!schoolId,
  });
  // Prefer published year, then most recent draft
  const year = useMemo(() => {
    const published = academicYears.find((y) => y.status === 'published');
    return published?.name || academicYears[0]?.name || '';
  }, [academicYears]);

  const { data: teachers = [], isLoading: loadingTeachers } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolId }),
    queryFn: () => getTeachersDirect({ school_id: schoolId }),
    enabled: !!schoolId,
  });

  const { data: classes = [], isLoading: loadingClasses } = useQuery({
    queryKey: queryKeys.classes.list({ school_id: schoolId }),
    queryFn: () => getClassesDirect(schoolId),
    enabled: !!schoolId,
  });

  const assocQueryKey = useMemo(
    () => [...queryKeys.teachers.all, 'class-associations', schoolId, year] as const,
    [schoolId, year]
  );

  const { data: associations = [], isLoading: loadingAssoc } = useQuery({
    queryKey: assocQueryKey,
    queryFn: () => getTeacherClassAssociations({ academicYear: year }),
    enabled: !!schoolId && !!year,
  });

  // Sync server → local state whenever associations load for a new (schoolId, year) combo
  useEffect(() => {
    if (loadingAssoc || !year) return;
    const syncKey = `${schoolId}|${year}`;
    const map = new Map<string, string>();
    const set = new Set<string>();
    for (const a of associations) {
      const key = `${a.teacher_id}|${a.class_id}`;
      map.set(key, a.id);
      set.add(key);
    }
    setServerState(map);
    // Reset draft whenever we load a fresh context (new school or year)
    if (syncedForRef.current !== syncKey) {
      setDraft(new Set(set));
      syncedForRef.current = syncKey;
    }
  }, [associations, loadingAssoc, schoolId, year]);

  const sortedClasses = useMemo(
    () => [...classes].sort((a, b) => a.class_name.localeCompare(b.class_name, undefined, { numeric: true })),
    [classes]
  );

  const filteredTeachers = useMemo(
    () => teachers.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())),
    [teachers, search]
  );

  // Diff: what changed vs server
  const toCreate = useMemo(() => [...draft].filter((k) => !serverState.has(k)), [draft, serverState]);
  const toDelete = useMemo(() => [...serverState.keys()].filter((k) => !draft.has(k)), [draft, serverState]);
  const hasChanges = toCreate.length > 0 || toDelete.length > 0;

  const handleToggle = useCallback((teacherId: string, classId: string) => {
    const key = `${teacherId}|${classId}`;
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleSelectAllForTeacher = useCallback((teacherId: string) => {
    const allKeys = sortedClasses.map((c) => `${teacherId}|${c.id}`);
    const allChecked = allKeys.every((k) => draft.has(k));
    setDraft((prev) => {
      const next = new Set(prev);
      if (allChecked) allKeys.forEach((k) => next.delete(k));
      else allKeys.forEach((k) => next.add(k));
      return next;
    });
  }, [sortedClasses, draft]);

  const handleSelectAllForClass = useCallback((classId: string) => {
    const allKeys = filteredTeachers.map((t) => `${t.id}|${classId}`);
    const allChecked = allKeys.every((k) => draft.has(k));
    setDraft((prev) => {
      const next = new Set(prev);
      if (allChecked) allKeys.forEach((k) => next.delete(k));
      else allKeys.forEach((k) => next.add(k));
      return next;
    });
  }, [filteredTeachers, draft]);

  const handleDiscard = () => {
    const set = new Set<string>(serverState.keys());
    setDraft(set);
  };

  const handleSave = async () => {
    if (!year) {
      toast.error('No academic year found. Please create one first.');
      return;
    }
    setSaving(true);
    try {
      const creates = toCreate.map((k) => {
        const [teacher_id, class_id] = k.split('|');
        return createTeacherClassAssociation({ teacher_id, class_id, academic_year: year });
      });
      const deletes = toDelete.map((k) => {
        const id = serverState.get(k)!;
        return deleteTeacherClassAssociation(id);
      });
      await Promise.all([...creates, ...deletes]);

      toast.success(`Saved — ${toCreate.length} added, ${toDelete.length} removed`);
      // Reset sync key so the upcoming refetch re-populates both serverState and draft with real IDs
      syncedForRef.current = '';
      queryClient.invalidateQueries({ queryKey: [...queryKeys.teachers.all, 'class-associations'] });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save changes');
      handleDiscard(); // roll back draft on error
    } finally {
      setSaving(false);
    }
  };

  const isLoading = loadingYears || loadingTeachers || loadingClasses || loadingAssoc;

  if (!schoolId) return <Alert severity="warning">No school selected.</Alert>;
  if (!year && !isLoading) return (
    <Alert severity="info" sx={{ mt: 2 }}>
      No academic year found. Please create one in Settings before managing teacher-class assignments.
    </Alert>
  );

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6">Teacher-Class Assignments</Typography>
          <Tooltip title="Tick the classes each teacher is assigned to. The suggestion engine and auto-fill strongly prefer teachers linked to a class.">
            <InfoIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'default' }} />
          </Tooltip>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          {hasChanges && (
            <Chip
              label={`${toCreate.length + toDelete.length} unsaved`}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<UndoIcon />}
            onClick={handleDiscard}
            disabled={!hasChanges || saving}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            Save Changes
          </Button>
        </Stack>
      </Stack>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search teachers…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        sx={{ mb: 2, width: 260 }}
      />

      {saving && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto', borderRadius: 2 }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {/* Teacher column header */}
                <TableCell
                  sx={{
                    minWidth: 220,
                    fontWeight: 700,
                    position: 'sticky',
                    left: 0,
                    zIndex: 4,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                    borderRight: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Teacher
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight={400}>
                    {teachers.length} total
                  </Typography>
                </TableCell>

                {/* Class column headers — click to select all in column */}
                {sortedClasses.map((cls) => {
                  const colKeys = filteredTeachers.map((t) => `${t.id}|${cls.id}`);
                  const allChecked = colKeys.length > 0 && colKeys.every((k) => draft.has(k));
                  const someChecked = colKeys.some((k) => draft.has(k));
                  return (
                    <TableCell
                      key={cls.id}
                      align="center"
                      sx={{
                        fontWeight: 600,
                        minWidth: 90,
                        cursor: 'pointer',
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                        userSelect: 'none',
                      }}
                      onClick={() => handleSelectAllForClass(cls.id)}
                    >
                      <Tooltip title={`Click to ${allChecked ? 'deselect' : 'select'} all for ${cls.class_name}`}>
                        <Box>
                          <SchoolIcon sx={{ fontSize: 14, color: 'text.secondary', display: 'block', mx: 'auto', mb: 0.25 }} />
                          <Typography variant="caption" fontWeight={600} noWrap>{cls.class_name}</Typography>
                          <Checkbox
                            checked={allChecked}
                            indeterminate={someChecked && !allChecked}
                            size="small"
                            sx={{ p: 0, display: 'block', mx: 'auto' }}
                            tabIndex={-1}
                            disableRipple
                            onChange={() => handleSelectAllForClass(cls.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredTeachers.map((teacher) => {
                const assignedCount = sortedClasses.filter((c) => draft.has(`${teacher.id}|${c.id}`)).length;
                const serverCount = sortedClasses.filter((c) => serverState.has(`${teacher.id}|${c.id}`)).length;
                const rowChanged = assignedCount !== serverCount;
                const allRowChecked = sortedClasses.length > 0 && sortedClasses.every((c) => draft.has(`${teacher.id}|${c.id}`));

                return (
                  <TableRow
                    key={teacher.id}
                    hover
                    sx={{
                      bgcolor: rowChanged ? alpha(theme.palette.warning.main, 0.04) : undefined,
                    }}
                  >
                    {/* Teacher name cell */}
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        bgcolor: rowChanged
                          ? alpha(theme.palette.warning.main, 0.06)
                          : theme.palette.background.paper,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        py: 0.5,
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <Tooltip title={allRowChecked ? 'Deselect all classes' : 'Select all classes'}>
                          <Checkbox
                            checked={allRowChecked}
                            indeterminate={assignedCount > 0 && !allRowChecked}
                            size="small"
                            onChange={() => handleSelectAllForTeacher(teacher.id)}
                            sx={{ p: 0.25, flexShrink: 0 }}
                            inputProps={{ 'aria-label': `Select all classes for ${teacher.name}` }}
                          />
                        </Tooltip>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" alignItems="center" gap={0.75}>
                            <Tooltip
                              title={
                                <Box>
                                  <Typography variant="caption" display="block" sx={{ fontWeight: 600, mb: 0.25 }}>Subjects:</Typography>
                                  {teacher.subjects && teacher.subjects.length > 0
                                    ? teacher.subjects.map((s) => (
                                        <Typography key={s.id} variant="caption" display="block" sx={{ pl: 1, opacity: 0.9 }}>
                                          • {s.subject_name}
                                        </Typography>
                                      ))
                                    : <Typography variant="caption" display="block" sx={{ pl: 1, opacity: 0.7 }}>None assigned</Typography>
                                  }
                                </Box>
                              }
                              placement="right"
                              arrow
                            >
                              <Typography variant="body2" fontWeight={500} noWrap sx={{ cursor: 'default' }}>{teacher.name}</Typography>
                            </Tooltip>
                            {assignedCount > 0 && (
                              <Typography variant="caption" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                                <CheckCircleIcon sx={{ fontSize: 11 }} />
                                {assignedCount}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                    </TableCell>

                    {/* Checkbox cells */}
                    {sortedClasses.map((cls) => {
                      const key = `${teacher.id}|${cls.id}`;
                      const checked = draft.has(key);
                      const wasChecked = serverState.has(key);
                      const cellChanged = checked !== wasChecked;

                      return (
                        <TableCell
                          key={cls.id}
                          align="center"
                          padding="checkbox"
                          sx={{
                            py: 0.25,
                            bgcolor: checked
                              ? alpha(theme.palette.primary.main, cellChanged ? 0.12 : 0.06)
                              : cellChanged
                              ? alpha(theme.palette.error.main, 0.06)
                              : undefined,
                            transition: 'background-color 0.15s',
                          }}
                        >
                          <Checkbox
                            checked={checked}
                            onChange={() => handleToggle(teacher.id, cls.id)}
                            size="small"
                            color="primary"
                            disabled={saving}
                            inputProps={{ 'aria-label': `Assign ${teacher.name} to ${cls.class_name}` }}
                            sx={{
                              transition: 'transform 0.1s',
                              '&:hover': { transform: 'scale(1.15)' },
                            }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}

              {filteredTeachers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={sortedClasses.length + 1} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No teachers match "{search}"
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Footer summary */}
      {!isLoading && (
        <Stack direction="row" gap={2} sx={{ mt: 1.5 }} flexWrap="wrap" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {serverState.size} total assignments · {teachers.length} teachers · {sortedClasses.length} classes
            {year && ` · ${year}`}
          </Typography>
          {hasChanges && (
            <Typography variant="caption" color="warning.main">
              {toCreate.length > 0 && `+${toCreate.length} to add`}
              {toCreate.length > 0 && toDelete.length > 0 && ' · '}
              {toDelete.length > 0 && `−${toDelete.length} to remove`}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}
